import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Copy, CheckCircle, ArrowRight } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

const PaymentSuccess = () => {
  const [unlockCode, setUnlockCode] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  useEffect(() => {
    const fetchUnlockCode = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('assigned_unlock_code')
            .eq('id', user.id)
            .single()
          
          if (profile?.assigned_unlock_code) {
            setUnlockCode(profile.assigned_unlock_code)
          }
        }
      } catch (error) {
        console.error('Error fetching unlock code:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUnlockCode()
  }, [])

  const handleCopyCode = () => {
    navigator.clipboard.writeText(unlockCode)
    toast({
      title: "Code copied!",
      description: "Your unlock code has been copied to clipboard.",
    })
  }

  const handleReturnToDashboard = () => {
    navigate('/dashboard')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100 p-4">
      <div className="w-full max-w-2xl">
        <Card className="shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Payment Successful! 🎉
            </CardTitle>
            <CardDescription className="text-lg">
              Your payment has been processed successfully
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Success Message */}
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <h3 className="font-semibold text-green-900 mb-2">Thank you for your payment!</h3>
              <p className="text-green-800">
                You can now unlock your account and access all premium features.
              </p>
            </div>

            {/* Unlock Code Display */}
            {unlockCode && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-700">Your Unlock Code</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyCode}
                    className="flex items-center gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </Button>
                </div>
                <div className="bg-white p-3 rounded border-2 border-dashed border-gray-300">
                  <code className="text-lg font-mono tracking-wider text-center block text-green-600">
                    {unlockCode}
                  </code>
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  Use this code to unlock your account
                </p>
              </div>
            )}

            {/* Next Steps */}
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Next Steps:</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-sm font-semibold text-green-600">
                    1
                  </div>
                  <div>
                    <h5 className="font-medium">Return to the Platform</h5>
                    <p className="text-gray-600">Click the button below to go back to your dashboard</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-sm font-semibold text-green-600">
                    2
                  </div>
                  <div>
                    <h5 className="font-medium">Enter Your Unlock Code</h5>
                    <p className="text-gray-600">You'll see a modal asking for your unlock code</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-sm font-semibold text-green-600">
                    3
                  </div>
                  <div>
                    <h5 className="font-medium">Start Learning!</h5>
                    <p className="text-gray-600">Enjoy full access to all premium features</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <Button
              onClick={handleReturnToDashboard}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg"
              size="lg"
            >
              <ArrowRight className="mr-2 h-5 w-5" />
              Return to Dashboard
            </Button>

            {/* Additional Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Need Help?</h4>
              <p className="text-sm text-blue-800">
                If you have any issues unlocking your account, please contact our support team at{' '}
                <a href="mailto:lucas@dably.co" className="text-blue-600 hover:underline">
                  lucas@dably.co
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PaymentSuccess
